"use client";
import { Box, Button, Text, useBreakpointValue, Wrap, WrapItem } from "@chakra-ui/react";
import { Tooltip } from "../ui/tooltip";
import { sortSizes } from "@/utils/sizes";
import { useTranslations } from "next-intl";
import { ToggleTip } from "../ui/toggle-tip";

export type SizeStock = {
  sizeId: string | null;
  sizeName: string | null;
  amount: number;
};


export default function SizeSelector({
  stocks,
  selected,
  onChange,
}: {
  stocks: SizeStock[];
  selected: string | null;
  onChange: (sizeName: string | null) => void;
}) {
  const t = useTranslations();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const sizes = stocks.filter(s => s.sizeId && s.sizeName);

  if (!sizes.length) return null;

  const sortedSizes: SizeStock[] = sortSizes(sizes);

  return (
    <Box>
      <Text textStyle="regular16" fontWeight="400" mb="8px">
        {t("size")}:
      </Text>

      <Wrap gap={2} mb={2}>
        {sortedSizes.map((size) => {
          const isDisabled = size.amount <= 0;

          const button = (
            <Button
              onClick={(e) => {
                if (isDisabled) return;
                onChange(size.sizeName);
              }}
              minW="56px"
              h="56px"
              textStyle="bold16"
              textTransform="uppercase"
              variant="plain"
              borderRadius="none"
              color={isDisabled ? "stroke" : "black"}
              border={
                selected === size.sizeName
                  ? "2px solid #000"
                  : "1px solid #BDBDBD"
              }
              boxShadow={
                selected === size.sizeName
                  ? "0 0 0 2px #000"
                  : undefined
              }
              transition="box-shadow 0.2s, border 0.2s"
              _hover={
                (selected === size.sizeName) || isDisabled
                  ? undefined
                  : {
                    borderColor: "accent",
                    boxShadow:
                      "0 0 0 1px var(--chakra-colors-accent)",
                    bg: "accent.secondary",
                  }
              }
              cursor={isDisabled ? "not-allowed" : "pointer"}
            >
              {size.sizeName}
            </Button>
          );

          return (
            <WrapItem key={size.sizeId}>
              {isMobile ? (
                (!isDisabled ? (
                  button
                ) : (
                  <ToggleTip
                    content={t("out_of_stock")}
                  >
                    {button}
                  </ToggleTip>
                ))
              ) : (
                <Tooltip
                  content={t("out_of_stock")}
                  disabled={!isDisabled}
                  openDelay={100}
                >
                  {button}
                  </Tooltip>
              )}
            </WrapItem>
          );
        })}
      </Wrap>

      {selected && (() => {
        const remaining =
          sizes.find((s) => s.sizeName === selected)?.amount ?? 0;

        return remaining < 10 ? (
          <Text fontSize="sm" color="brand">
            Осталось: {remaining} шт
          </Text>
        ) : null;
      })()}
    </Box>
  );
}
